import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { Inline } from '../atoms/Inline/Inline';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import type { BudgetCategory, BudgetExpense, BudgetSummary, RecurringExpense } from '../../services/budgetService';
import { formatSpentOn, monthLabel, monthsElapsed, percentChange } from './budgetFormat';
import { useMoney } from './useMoney';
import './BudgetPageParts.css';

/** Colour key of a category (legend, tables, list) — always next to its name, never on its own. */
export function CategorySwatch({ category }: { category: BudgetCategory }) {
  return <span className={`budget-swatch budget-swatch--${category}`} aria-hidden="true" />;
}

/** "12 % more than …" / "8 % less than …" — the direction in words, not only a sign or a colour. */
export function ChangeText({ current, previous, label }: { current: number; previous: number; label: string }) {
  const { t } = useTranslation();
  const change = percentChange(current, previous);
  if (change === null) return <Text size="sm" color="muted">{t('budget.change.noBase', { label })}</Text>;
  if (change === 0) return <Text size="sm" color="secondary">{t('budget.change.same', { label })}</Text>;
  return (
    <Text size="sm" color="secondary">
      {t(change > 0 ? 'budget.change.more' : 'budget.change.less', { percent: Math.abs(change), label })}
    </Text>
  );
}

function Kpi({ label, value, children, testId }: { label: string; value: string; children?: ReactNode; testId: string }) {
  return (
    <Card className="budget-kpi" data-testid={testId}>
      <Stack gap="1">
        <Text size="sm" color="secondary">{label}</Text>
        <Text as="span" className="budget-kpi__value">{value}</Text>
        {children}
      </Stack>
    </Card>
  );
}

/** The year in four numbers: spent, forecast (or monthly average for a past year), the previous year and the monthly average. */
export function BudgetKpis({ summary }: { summary: BudgetSummary }) {
  const { t } = useTranslation();
  const money = useMoney();
  const { year, totals } = summary;
  // Average over the months since the first expense of the year, not since January (audit §3.8).
  const firstSpendMonth = summary.months.find((m) => m.totalCents > 0)?.month ?? 1;
  const elapsed = Math.max(1, monthsElapsed(summary) - firstSpendMonth + 1);
  const previousLabel = summary.isCurrentYear ? t('budget.kpi.samePeriodLabel', { year: year - 1 }) : String(year - 1);
  return (
    <div className="budget-kpis">
      <Kpi testId="budget-kpi-spent" label={t(summary.isCurrentYear ? 'budget.kpi.spentSoFar' : 'budget.kpi.spent', { year })} value={money.format(totals.totalCents)}>
        <ChangeText current={totals.totalCents} previous={totals.previousSamePeriodCents} label={previousLabel} />
      </Kpi>
      {totals.projectedTotalCents !== null && (
        <Kpi testId="budget-kpi-forecast" label={t('budget.kpi.forecast', { year })} value={money.format(totals.projectedTotalCents)}>
          <ChangeText current={totals.projectedTotalCents} previous={totals.previousTotalCents} label={String(year - 1)} />
        </Kpi>
      )}
      <Kpi testId="budget-kpi-previous" label={t('budget.kpi.previousYear', { year: year - 1 })} value={money.format(totals.previousTotalCents)} />
      <Kpi
        testId="budget-kpi-average"
        label={t('budget.kpi.averagePerMonth')}
        value={money.format(Math.round(totals.totalCents / elapsed))}
      >
        <Text size="sm" color="muted">{t('budget.kpi.averageBasis', { count: elapsed })}</Text>
      </Kpi>
    </div>
  );
}

/**
 * A scroll container — a data table that scrolls horizontally on narrow
 * screens, or a long code block — focusable (keyboard scrolling) while it overflows.
 */
export function TableWrap({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const wrap = ref.current;
    if (wrap === null) return undefined;
    const update = (): void => {
      if (wrap.scrollWidth > wrap.clientWidth + 1 || wrap.scrollHeight > wrap.clientHeight + 1) wrap.setAttribute('tabindex', '0');
      else wrap.removeAttribute('tabindex');
    };
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(wrap);
    return () => observer.disconnect();
  });
  return (
    <div ref={ref} className={className ? `budget-table__wrap ${className}` : 'budget-table__wrap'} role="region" aria-label={label}>
      {children}
    </div>
  );
}

/** Spend per category: this year, the previous year (same period), the change, the monthly rate and the forecast. */
export function CategoryTable({ summary, categories }: { summary: BudgetSummary; categories: readonly BudgetCategory[] }) {
  const { t } = useTranslation();
  const money = useMoney();
  const cell = (cents: number | null) => (cents === null ? '–' : money.format(cents));
  const rows = summary.categories.filter((c) => categories.includes(c.category));
  const caption = t('budget.table.categoryCaption', { year: summary.year });
  const previousHeader = summary.isCurrentYear ? t('budget.kpi.samePeriodLabel', { year: summary.year - 1 }) : String(summary.year - 1);
  const changeText = (current: number, previous: number) => {
    const change = percentChange(current, previous);
    return change === null ? '–' : `${change > 0 ? '+' : ''}${change} %`;
  };
  return (
    <TableWrap label={caption}>
      <table className="budget-table" data-testid="budget-category-table">
        <caption className="budget-table__caption">{caption}</caption>
        <thead>
          <tr>
            <th scope="col">{t('budget.table.category')}</th>
            <th scope="col" className="budget-table__num">{summary.year}</th>
            <th scope="col" className="budget-table__num">{previousHeader}</th>
            <th scope="col" className="budget-table__num">{t('budget.table.change')}</th>
            {summary.isCurrentYear && <th scope="col" className="budget-table__num">{t('budget.table.rate')}</th>}
            {summary.isCurrentYear && <th scope="col" className="budget-table__num">{t('budget.table.forecast', { year: summary.year })}</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.category}>
              <th scope="row">
                <span className="budget-table__category">
                  <CategorySwatch category={c.category} />
                  {t(`budget.categories.${c.category}`)}
                </span>
              </th>
              <td className="budget-table__num">{cell(c.totalCents)}</td>
              <td className="budget-table__num">{cell(c.previousSamePeriodCents)}</td>
              <td className="budget-table__num">{changeText(c.totalCents, c.previousSamePeriodCents)}</td>
              {summary.isCurrentYear && <td className="budget-table__num">{cell(c.monthlyRateCents)}</td>}
              {summary.isCurrentYear && <td className="budget-table__num">{cell(c.projectedTotalCents)}</td>}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">{t('budget.table.total')}</th>
            <td className="budget-table__num">{cell(summary.totals.totalCents)}</td>
            <td className="budget-table__num">{cell(summary.totals.previousSamePeriodCents)}</td>
            <td className="budget-table__num">{changeText(summary.totals.totalCents, summary.totals.previousSamePeriodCents)}</td>
            {summary.isCurrentYear && (
              <td className="budget-table__num">
                {cell(summary.categories.reduce((sum, c) => sum + (c.monthlyRateCents ?? 0), 0))}
              </td>
            )}
            {summary.isCurrentYear && <td className="budget-table__num">{cell(summary.totals.projectedTotalCents)}</td>}
          </tr>
        </tfoot>
      </table>
    </TableWrap>
  );
}

/** Month by month: this year, the previous year and (current year) the forecast — the text equivalent of the charts. */
export function MonthTable({ summary }: { summary: BudgetSummary }) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  const money = useMoney();
  const elapsed = monthsElapsed(summary);
  const caption = t('budget.table.monthCaption', { year: summary.year, previous: summary.year - 1 });
  return (
    <TableWrap label={caption}>
      <table className="budget-table" data-testid="budget-month-table">
        <caption className="budget-table__caption">{caption}</caption>
        <thead>
          <tr>
            <th scope="col">{t('budget.table.month')}</th>
            <th scope="col" className="budget-table__num">{summary.year}</th>
            <th scope="col" className="budget-table__num">{summary.year - 1}</th>
            {summary.isCurrentYear && <th scope="col" className="budget-table__num">{t('budget.table.forecastShort')}</th>}
          </tr>
        </thead>
        <tbody>
          {summary.months.map((m, i) => (
            <tr key={m.month}>
              <th scope="row">{monthLabel(m.month, language, 'long')}</th>
              <td className="budget-table__num">{m.month <= elapsed ? money.format(m.totalCents) : '–'}</td>
              <td className="budget-table__num">{money.format(summary.previousMonths[i]?.totalCents ?? 0)}</td>
              {summary.isCurrentYear && (
                <td className="budget-table__num">{m.month >= elapsed && m.projectedCents !== null ? money.format(m.projectedCents) : '–'}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

export interface ExpenseListProps {
  expenses: readonly BudgetExpense[];
  onEdit: (expense: BudgetExpense) => void;
  onDelete: (expense: BudgetExpense) => void;
  deleting: boolean;
}

export function ExpenseList({ expenses, onEdit, onDelete, deleting }: ExpenseListProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  const money = useMoney();
  return (
    <ul className="budget-expenses" data-testid="budget-expense-list">
      {expenses.map((e) => {
        const category = String(t(`budget.categories.${e.category}`));
        const amount = money.format(e.amountCents);
        const date = formatSpentOn(e.spentOn, language);
        return (
          <li key={e.id} className="budget-expenses__item">
            <Inline gap="3" align="center" wrap className="budget-expenses__row">
              <Stack gap="1" className="budget-expenses__text">
                <span className="budget-table__category">
                  <CategorySwatch category={e.category} />
                  <Text as="span" weight="semibold">{category}</Text>
                  {e.recurringId && <Badge tone="info">{t('budget.expenses.recurringBadge')}</Badge>}
                </span>
                <Text size="sm" color="secondary">
                  {e.note ? t('budget.expenses.dateNote', { date, note: e.note }) : date}
                </Text>
              </Stack>
              <Text as="span" weight="semibold" className="budget-expenses__amount">{amount}</Text>
              <Inline gap="1">
                <Button size="sm" variant="ghost" onClick={() => onEdit(e)} aria-label={t('budget.expenses.editLabel', { category, amount, date })}>
                  {t('budget.expenses.edit')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onDelete(e)}
                  disabled={deleting}
                  aria-label={t('budget.expenses.deleteLabel', { category, amount, date })}
                >
                  {t('budget.expenses.delete')}
                </Button>
              </Inline>
            </Inline>
          </li>
        );
      })}
    </ul>
  );
}

export interface RecurringListProps {
  items: readonly RecurringExpense[];
  /** YYYY-MM-DD (local). */
  today: string;
  onEdit: (item: RecurringExpense) => void;
  onEnd: (item: RecurringExpense) => void;
  onDelete: (item: RecurringExpense) => void;
  busy: boolean;
}

/** The recurring costs: amount and rhythm, the next booking (or that it ended), and edit / end / delete. */
export function RecurringList({ items, today, onEdit, onEnd, onDelete, busy }: RecurringListProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  const money = useMoney();
  return (
    <ul className="budget-expenses" data-testid="budget-recurring-list">
      {items.map((r) => {
        const category = String(t(`budget.categories.${r.category}`));
        const amount = money.format(r.amountCents);
        const every = String(t('budget.recurring.every', { count: r.intervalMonths }));
        const started = r.startOn <= today;
        let schedule: string;
        if (r.nextOn === null) schedule = String(t('budget.recurring.ended', { date: formatSpentOn(r.endOn ?? r.startOn, language) }));
        else if (!started) schedule = String(t('budget.recurring.starts', { date: formatSpentOn(r.startOn, language) }));
        else schedule = String(t('budget.recurring.next', { date: formatSpentOn(r.nextOn, language) }));
        if (r.nextOn !== null && r.endOn) schedule = String(t('budget.recurring.until', { schedule, date: formatSpentOn(r.endOn, language) }));
        return (
          <li key={r.id} className="budget-expenses__item">
            <Inline gap="3" align="center" wrap className="budget-expenses__row">
              <Stack gap="1" className="budget-expenses__text">
                <span className="budget-table__category">
                  <CategorySwatch category={r.category} />
                  <Text as="span" weight="semibold">{category}</Text>
                  {r.nextOn === null && <Badge tone="neutral">{t('budget.recurring.endedBadge')}</Badge>}
                </span>
                <Text size="sm" color="secondary">{r.note ? t('budget.expenses.dateNote', { date: schedule, note: r.note }) : schedule}</Text>
              </Stack>
              <Stack gap="0" className="budget-recurring__amount">
                <Text as="span" weight="semibold" className="budget-expenses__amount">{amount}</Text>
                <Text as="span" size="sm" color="secondary">{every}</Text>
              </Stack>
              <Inline gap="1">
                <Button size="sm" variant="ghost" onClick={() => onEdit(r)} aria-label={t('budget.recurring.editLabel', { category, amount, every })}>
                  {t('budget.expenses.edit')}
                </Button>
                {r.nextOn !== null && started && (
                  <Button size="sm" variant="ghost" onClick={() => onEnd(r)} disabled={busy} aria-label={t('budget.recurring.endLabel', { category, amount, every })}>
                    {t('budget.recurring.end')}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => onDelete(r)} disabled={busy} aria-label={t('budget.recurring.deleteLabel', { category, amount, every })}>
                  {t('budget.expenses.delete')}
                </Button>
              </Inline>
            </Inline>
          </li>
        );
      })}
    </ul>
  );
}
